/**
 * Step6ReviewSubmit
 * Final review of all onboarding data with read-only summary cards,
 * edit-per-section, terms agreement, certification checkbox, and submit.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

const PROVINCES_MAP = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
};

const SECTORS_MAP = {
  healthcare: 'Healthcare', telecom: 'Telecom', insurance: 'Insurance',
  finance: 'Finance', utilities: 'Utilities', 'home-services': 'Home Services',
  retail: 'Retail', travel: 'Travel', legal: 'Legal', education: 'Education',
};

const LANGUAGES_MAP = {
  en: 'English', fr: 'French', 'en-fr': 'English & French',
  zh: 'Mandarin/Cantonese', pa: 'Punjabi', es: 'Spanish',
  tl: 'Tagalog', ar: 'Arabic', other: 'Other',
};

function ReviewCard({ title, stepNumber, onEdit, children }) {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '10px',
      border: '1px solid #e2e8f0',
      padding: '20px',
      marginBottom: '16px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          {title}
        </h3>
        <button
          type="button"
          onClick={() => onEdit(stepNumber)}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#3b82f6',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Edit
        </button>
      </div>
      <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.7' }}>
        {children}
      </div>
    </div>
  );
}

function DataRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '2px' }}>
      <span style={{ fontWeight: '600', color: '#374151', minWidth: '140px', flexShrink: 0 }}>{label}:</span>
      <span>{value}</span>
    </div>
  );
}

export function validateStep6(data) {
  const errors = {};
  if (!data.agreeTerms) errors.agreeTerms = 'You must agree to the terms and conditions';
  if (!data.certifyAccurate) errors.certifyAccurate = 'You must certify that the information is accurate';
  return errors;
}

export default function Step6ReviewSubmit({
  allData,
  reviewData,
  onChange,
  errors,
  onEdit,
  onSubmit,
  isSubmitting,
  submitError,
}) {
  const step1 = allData.step1 || {};
  const step2 = allData.step2 || {};
  const step3 = allData.step3 || {};
  const step4 = allData.step4 || {};
  const step5 = allData.step5 || {};

  const handleToggle = useCallback((e) => {
    const { name, checked } = e.target;
    onChange({ ...reviewData, [name]: checked });
  }, [reviewData, onChange]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Review & Submit</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Review your information below. Click "Edit" on any section to make changes.
        </p>
      </div>

      {/* Step 1: Business Info */}
      <ReviewCard title="Business Information" stepNumber={1} onEdit={onEdit}>
        <DataRow label="Business Name" value={step1.businessName} />
        <DataRow label="Legal Name" value={step1.legalName} />
        <DataRow label="Business Number" value={step1.businessNumber} />
        <DataRow label="Sector" value={SECTORS_MAP[step1.sector]} />
        <DataRow label="Province" value={PROVINCES_MAP[step1.province]} />
        <DataRow label="City" value={step1.city} />
        <DataRow label="Year Established" value={step1.yearEstablished} />
        <DataRow label="Employees" value={step1.employeeRange} />
        {step1.description && (
          <div style={{ marginTop: '8px' }}>
            <span style={{ fontWeight: '600', color: '#374151' }}>Description:</span>
            <p style={{ margin: '4px 0 0', color: '#64748b', lineHeight: '1.5' }}>{step1.description}</p>
          </div>
        )}
        {step1.logoFile && (
          <DataRow label="Logo" value={step1.logoFile.name} />
        )}
      </ReviewCard>

      {/* Step 2: Contact Details */}
      <ReviewCard title="Contact & Online Presence" stepNumber={2} onEdit={onEdit}>
        <DataRow label="Contact Name" value={step2.contactName} />
        <DataRow label="Contact Email" value={step2.contactEmail} />
        <DataRow label="Contact Phone" value={step2.contactPhone} />
        <DataRow label="Business Phone" value={step2.businessPhone} />
        <DataRow label="Toll-Free" value={step2.tollFreeNumber} />
        {(step2.streetAddress || step2.addressCity) && (
          <DataRow
            label="Address"
            value={[step2.streetAddress, step2.addressCity, PROVINCES_MAP[step2.addressProvince], step2.postalCode].filter(Boolean).join(', ')}
          />
        )}
        <DataRow label="Website" value={step2.website} />
        <DataRow label="LinkedIn" value={step2.linkedin} />
        <DataRow label="Facebook" value={step2.facebook} />
        <DataRow label="Twitter / X" value={step2.twitter} />
        <DataRow label="Instagram" value={step2.instagram} />
      </ReviewCard>

      {/* Step 3: Service Areas */}
      <ReviewCard title="Service Coverage" stepNumber={3} onEdit={onEdit}>
        <DataRow
          label="Provinces"
          value={(step3.provinces || []).map((p) => PROVINCES_MAP[p] || p).join(', ') || 'None selected'}
        />
        {step3.provinces && step3.provinces.length > 0 && step3.cities && (
          <div style={{ marginTop: '4px' }}>
            {step3.provinces.map((prov) => {
              const cities = step3.cities?.[prov];
              if (!cities || cities.length === 0) return null;
              return (
                <DataRow
                  key={prov}
                  label={`${PROVINCES_MAP[prov]} cities`}
                  value={cities.join(', ')}
                />
              );
            })}
          </div>
        )}
        <DataRow label="Service Radius" value={step3.serviceRadius ? `${step3.serviceRadius} km` : ''} />
        <DataRow label="Remote Services" value={step3.remoteServices ? 'Yes' : 'No'} />
        <DataRow
          label="Languages"
          value={(step3.languages || []).map((l) => LANGUAGES_MAP[l] || l).join(', ') || 'None selected'}
        />
      </ReviewCard>

      {/* Step 4: Pricing */}
      <ReviewCard title="Pricing" stepNumber={4} onEdit={onEdit}>
        {(step4.plans || []).length === 0 ? (
          <span style={{ color: '#94a3b8' }}>No plans configured</span>
        ) : (
          (step4.plans || []).map((plan, i) => (
            <div key={i} style={{
              padding: '8px 12px',
              backgroundColor: '#f8fafc',
              borderRadius: '6px',
              marginBottom: '8px',
              border: '1px solid #f1f5f9',
            }}>
              <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                {plan.name || `Plan ${i + 1}`}
              </div>
              {plan.contactForPricing ? (
                <span style={{ color: '#3b82f6' }}>Contact for Pricing</span>
              ) : plan.isFreeTier ? (
                <span style={{ color: '#22c55e', fontWeight: '600' }}>Free</span>
              ) : (
                <span>
                  {plan.monthlyPrice && `$${plan.monthlyPrice}/mo`}
                  {plan.annualPrice && ` | $${plan.annualPrice}/yr`}
                  {plan.setupFee && plan.setupFee !== '0' && ` | $${plan.setupFee} setup`}
                </span>
              )}
              {plan.features && (
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Features: {plan.features}
                </div>
              )}
            </div>
          ))
        )}
        <DataRow label="Pricing Pledge" value={step4.pricingPledge ? 'Agreed' : 'Not agreed'} />
      </ReviewCard>

      {/* Step 5: Verification */}
      <ReviewCard title="Verification & Compliance" stepNumber={5} onEdit={onEdit}>
        <DataRow label="ISED Number" value={step5.isedBusinessNumber} />
        <DataRow label="Provincial License" value={step5.provincialLicense} />
        <DataRow label="BBB Accredited" value={step5.bbbAccredited ? `Yes${step5.bbbId ? ` (${step5.bbbId})` : ''}` : 'No'} />
        {(step5.certifications || []).length > 0 && (
          <div style={{ marginTop: '4px' }}>
            <span style={{ fontWeight: '600', color: '#374151' }}>Certifications:</span>
            {step5.certifications.map((cert, i) => (
              <div key={i} style={{ marginLeft: '12px' }}>
                {cert.name}{cert.issuedBy ? ` (${cert.issuedBy})` : ''}{cert.year ? ` - ${cert.year}` : ''}
              </div>
            ))}
          </div>
        )}
        <DataRow label="Insurance" value={step5.hasInsurance ? `Yes${step5.insuranceProvider ? ` - ${step5.insuranceProvider}` : ''}` : 'No'} />
        <DataRow label="Business License" value={step5.businessLicenseFile?.name} />
        <DataRow label="Insurance Cert" value={step5.insuranceCertFile?.name} />
        <DataRow label="Privacy Policy" value={step5.privacyPolicyUrl} />
        <DataRow label="Terms of Service" value={step5.termsUrl} />
      </ReviewCard>

      {/* Agreement checkboxes */}
      <div style={{
        backgroundColor: '#fffbeb',
        borderRadius: '10px',
        border: '1px solid #fde68a',
        padding: '20px',
        marginBottom: '20px',
      }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              name="agreeTerms"
              checked={reviewData.agreeTerms || false}
              onChange={handleToggle}
              style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.5' }}>
              I agree to SM-Compare's{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                Privacy Policy
              </a>{' '}
              for provider listings. <span style={{ color: '#ef4444' }}>*</span>
            </span>
          </label>
          {errors.agreeTerms && <div style={errorStyle}>{errors.agreeTerms}</div>}
        </div>

        <div>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              name="certifyAccurate"
              checked={reviewData.certifyAccurate || false}
              onChange={handleToggle}
              style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.5' }}>
              I certify that all information provided in this application is accurate and complete
              to the best of my knowledge. I understand that providing false information may result
              in rejection or removal of my listing. <span style={{ color: '#ef4444' }}>*</span>
            </span>
          </label>
          {errors.certifyAccurate && <div style={errorStyle}>{errors.certifyAccurate}</div>}
        </div>
      </div>

      {/* Submit error */}
      {submitError && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#991b1b',
          marginBottom: '16px',
        }}>
          {submitError}
        </div>
      )}

      {/* Submit button */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={isSubmitting}
        style={{
          width: '100%',
          padding: '14px 32px',
          fontSize: '16px',
          fontWeight: '700',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: isSubmitting ? '#93c5fd' : '#3b82f6',
          color: '#fff',
          cursor: isSubmitting ? 'wait' : 'pointer',
          transition: 'background-color 0.15s',
        }}
      >
        {isSubmitting ? 'Submitting...' : 'Submit for Review'}
      </button>
    </div>
  );
}

Step6ReviewSubmit.propTypes = {
  allData: PropTypes.object.isRequired,
  reviewData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.string,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
  marginLeft: '26px',
};
